import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Phone, Calendar, CheckCircle, Inbox, DollarSign, Users, FileText, AlertTriangle,
  PhoneIncoming, Timer, Settings, X, ChevronUp, ChevronDown, ClipboardList
} from '../../icons/wp-icons';

// Lucas/H&D-Feedback 2026-05-08: Kennzahlen sollen wieder „auf einen Blick"
// sichtbar sein, ohne dass der Disponent zum Stats-Widget scrollen muss.
// Die KPI-Bar sitzt zwischen Page-Header und Widget-Grid, ist non-draggable
// und voll konfigurierbar (User wählt 4–6 KPIs).
//
// Datenquellen werden vom Dashboard.jsx als Props durchgereicht (kein eigener
// API-Call), damit wir keine zusätzliche Last produzieren.

const KPI_REGISTRY = {
  // HeyKiki nennt Anfragen durchgängig „Anrufe" (vgl. Navigation.jsx),
  // Standalone-Orgs bekommen Anfragen aus E-Mail/WhatsApp/Telefon → neutrales Label
  'offene-anrufe': {
    label: 'Offene Anrufe',
    labelStandalone: 'Offene Anfragen',
    icon: PhoneIncoming,
    iconStandalone: ClipboardList,
    accent: 'text-blue-600 bg-blue-50',
    linkTo: '/anfragen',
    getValue: ({ stats }) => stats?.anfragen ?? 0,
  },
  'termine-heute': {
    label: 'Termine heute',
    icon: Calendar,
    accent: 'text-purple-600 bg-purple-50',
    linkTo: '/kalender',
    getValue: ({ termineHeuteCount }) => termineHeuteCount ?? 0,
  },
  'meine-aufgaben': {
    label: 'Meine Aufgaben',
    icon: CheckCircle,
    accent: 'text-emerald-600 bg-emerald-50',
    linkTo: '/anfragen',
    getValue: ({ meineAnfragen, meineTermineHeute }) =>
      (meineAnfragen?.length || 0) + (meineTermineHeute?.length || 0),
  },
  'neue-anfragen': {
    label: 'Neue Anfragen',
    icon: Inbox,
    accent: 'text-amber-600 bg-amber-50',
    linkTo: '/anfragen',
    getValue: ({ stats }) => stats?.neueAnfragen ?? 0,
  },
  'umsatz-monat': {
    label: 'Umsatz Monat',
    icon: DollarSign,
    accent: 'text-green-600 bg-green-50',
    linkTo: '/rechnungen',
    requiresPermission: 'RECHNUNGEN',
    getValue: ({ statistiken }) => statistiken?.umsatzMonat ?? 0,
    format: (v) => formatEuroCompact(v),
  },
  'offene-forderungen': {
    label: 'Offene Forderungen',
    icon: AlertTriangle,
    accent: 'text-rose-600 bg-rose-50',
    linkTo: '/rechnungen',
    requiresPermission: 'RECHNUNGEN',
    getValue: ({ offeneForderungen }) => offeneForderungen?.gesamt?.summe ?? 0,
    format: (v) => formatEuroCompact(v),
  },
  'kvas-monat': {
    label: 'KVAs Monat',
    icon: FileText,
    accent: 'text-indigo-600 bg-indigo-50',
    linkTo: '/kostenvoranschlaege',
    getValue: ({ statistiken }) => statistiken?.kvasMonat ?? 0,
  },
  'kunden-gesamt': {
    label: 'Kunden gesamt',
    icon: Users,
    accent: 'text-cyan-600 bg-cyan-50',
    linkTo: '/kunden',
    getValue: ({ stats }) => stats?.kunden ?? 0,
  },
  // HeyKiki-spezifisch — nur sichtbar wenn isHeykikiOrg
  'anrufe-heute-ki': {
    label: 'Anrufe heute (KI)',
    icon: Phone,
    accent: 'text-blue-600 bg-blue-50',
    linkTo: '/ki-zentrale?tab=anrufe',
    heykikiOnly: true,
    getValue: ({ kiAnrufeHeuteCount }) => kiAnrufeHeuteCount ?? 0,
  },
  'anrufminuten-monat': {
    label: 'KI-Minuten Monat',
    icon: Timer,
    accent: 'text-purple-600 bg-purple-50',
    linkTo: '/ki-zentrale?tab=anrufe',
    heykikiOnly: true,
    getValue: ({ voiceRaw }) => voiceRaw?.currentMonthUsedMinutes ?? 0,
    format: (v, { voiceRaw }) => {
      const inkl = voiceRaw?.inklusivMinuten;
      return inkl ? `${v} / ${inkl}` : String(v);
    },
  },
};

const DEFAULT_STANDALONE = ['offene-anrufe', 'termine-heute', 'meine-aufgaben', 'neue-anfragen', 'umsatz-monat'];
const DEFAULT_HEYKIKI = ['offene-anrufe', 'termine-heute', 'anrufe-heute-ki', 'neue-anfragen', 'anrufminuten-monat'];

const MIN_KPIS = 3;
const MAX_KPIS = 6;

function formatEuroCompact(value) {
  const num = Number(value) || 0;
  if (num >= 1000000) return `${(num / 1000000).toFixed(1).replace('.', ',')}M €`;
  if (num >= 10000) return `${Math.round(num / 1000)}k €`;
  if (num >= 1000) return `${(num / 1000).toFixed(1).replace('.', ',')}k €`;
  return `${num.toFixed(0)} €`;
}

function storageKeyFor(userId) {
  return `dashboard-kpi-bar-v1_${userId || 'anon'}`;
}

export default function DashboardKpiBar({
  userId,
  isHeykikiOrg,
  canViewRechnungen,
  // data props
  stats,
  statistiken,
  offeneForderungen,
  meineAnfragen,
  meineTermineHeute,
  termineHeuteCount,
  voiceRaw,
  kiAnrufeHeuteCount,
}) {
  const defaultIds = isHeykikiOrg ? DEFAULT_HEYKIKI : DEFAULT_STANDALONE;
  const [selectedIds, setSelectedIds] = useState(() => {
    try {
      const raw = localStorage.getItem(storageKeyFor(userId));
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length >= MIN_KPIS) return parsed;
      }
    } catch {}
    return defaultIds;
  });
  const [showSettings, setShowSettings] = useState(false);

  // Wenn der Default-Set sich durch orgType-Wechsel ändert UND der User noch
  // keine Custom-Auswahl hat, springen wir auf den neuen Default.
  useEffect(() => {
    const raw = localStorage.getItem(storageKeyFor(userId));
    if (!raw) setSelectedIds(defaultIds);
  }, [isHeykikiOrg, userId]);

  const persistSelection = useCallback((ids) => {
    setSelectedIds(ids);
    try {
      localStorage.setItem(storageKeyFor(userId), JSON.stringify(ids));
    } catch {}
  }, [userId]);

  const dataContext = useMemo(() => ({
    stats, statistiken, offeneForderungen, meineAnfragen, meineTermineHeute,
    termineHeuteCount, voiceRaw, kiAnrufeHeuteCount,
  }), [stats, statistiken, offeneForderungen, meineAnfragen, meineTermineHeute,
       termineHeuteCount, voiceRaw, kiAnrufeHeuteCount]);

  // Filter: nur KPIs anzeigen, für die der User berechtigt ist und die zum orgType passen
  const isKpiAvailable = useCallback((id) => {
    const def = KPI_REGISTRY[id];
    if (!def) return false;
    if (def.heykikiOnly && !isHeykikiOrg) return false;
    if (def.requiresPermission === 'RECHNUNGEN' && !canViewRechnungen) return false;
    return true;
  }, [isHeykikiOrg, canViewRechnungen]);

  // Label/Icon je orgType auflösen (labelStandalone/iconStandalone als Override)
  const getKpiDef = useCallback((id) => {
    const def = KPI_REGISTRY[id];
    if (!def) return null;
    if (!isHeykikiOrg && (def.labelStandalone || def.iconStandalone)) {
      return {
        ...def,
        label: def.labelStandalone || def.label,
        icon: def.iconStandalone || def.icon,
      };
    }
    return def;
  }, [isHeykikiOrg]);

  const visibleKpis = selectedIds.filter(isKpiAvailable);
  const allAvailableKpis = Object.keys(KPI_REGISTRY).filter(isKpiAvailable);

  if (visibleKpis.length === 0) return null;

  return (
    <div className="mb-6">
      <div className="flex items-stretch gap-2 sm:gap-3 overflow-x-auto pb-1 snap-x snap-mandatory scrollbar-hide">
        {visibleKpis.map((id) => {
          const def = getKpiDef(id);
          if (!def) return null;
          const Icon = def.icon;
          const rawValue = def.getValue(dataContext);
          const display = def.format ? def.format(rawValue, dataContext) : String(rawValue);
          return (
            <Link
              key={id}
              to={def.linkTo}
              className="flex-shrink-0 snap-start min-w-[140px] sm:min-w-[160px] flex-1 flex items-center gap-3 bg-white rounded-xl border border-gray-200 px-3 sm:px-4 py-3 hover:border-primary/40 hover:shadow-sm transition-all"
            >
              <div className={`flex items-center justify-center w-9 h-9 rounded-lg ${def.accent}`}>
                <Icon className="w-5 h-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-xs text-gray-500 truncate">{def.label}</div>
                <div className="text-xl font-semibold text-gray-900 leading-tight truncate">{display}</div>
              </div>
            </Link>
          );
        })}
        <button
          type="button"
          onClick={() => setShowSettings(true)}
          className="flex-shrink-0 flex items-center justify-center w-10 px-2 bg-white rounded-xl border border-gray-200 text-gray-400 hover:text-gray-700 hover:border-gray-300 transition-colors"
          title="KPIs auswählen"
          aria-label="KPIs auswählen"
        >
          <Settings className="w-4 h-4" />
        </button>
      </div>

      {showSettings && (
        <KpiSettingsModal
          allAvailableIds={allAvailableKpis}
          selectedIds={selectedIds}
          getDef={getKpiDef}
          onClose={() => setShowSettings(false)}
          onSave={(ids) => {
            persistSelection(ids);
            setShowSettings(false);
          }}
          onResetDefault={() => {
            try { localStorage.removeItem(storageKeyFor(userId)); } catch {}
            setSelectedIds(defaultIds);
            setShowSettings(false);
          }}
        />
      )}
    </div>
  );
}

function KpiSettingsModal({ allAvailableIds, selectedIds, getDef, onClose, onSave, onResetDefault }) {
  const [draft, setDraft] = useState(() => selectedIds.filter(id => allAvailableIds.includes(id)));

  const isSelected = (id) => draft.includes(id);
  const toggle = (id) => {
    if (isSelected(id)) {
      if (draft.length <= MIN_KPIS) return;
      setDraft(draft.filter(x => x !== id));
    } else {
      if (draft.length >= MAX_KPIS) return;
      setDraft([...draft, id]);
    }
  };
  const move = (id, dir) => {
    const idx = draft.indexOf(id);
    const next = idx + dir;
    if (idx === -1 || next < 0 || next >= draft.length) return;
    const copy = [...draft];
    [copy[idx], copy[next]] = [copy[next], copy[idx]];
    setDraft(copy);
  };

  const unselected = allAvailableIds.filter(id => !draft.includes(id));

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold">KPI-Leiste anpassen</h3>
            <p className="text-xs text-gray-500 mt-0.5">{MIN_KPIS}–{MAX_KPIS} Werte auswählen, Reihenfolge per Pfeile.</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Aktiv ({draft.length})</h4>
            <div className="space-y-1.5">
              {draft.map((id, idx) => {
                const def = getDef(id);
                if (!def) return null;
                const Icon = def.icon;
                return (
                  <div key={id} className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200">
                    <div className={`w-7 h-7 rounded-md flex items-center justify-center ${def.accent}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <span className="flex-1 text-sm font-medium">{def.label}</span>
                    <button
                      type="button"
                      onClick={() => move(id, -1)}
                      disabled={idx === 0}
                      className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
                      aria-label="Nach oben"
                    >
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => move(id, 1)}
                      disabled={idx === draft.length - 1}
                      className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
                      aria-label="Nach unten"
                    >
                      <ChevronDown className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => toggle(id)}
                      disabled={draft.length <= MIN_KPIS}
                      className="p-1 text-gray-400 hover:text-red-500 disabled:opacity-30 disabled:cursor-not-allowed"
                      aria-label="Entfernen"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          {unselected.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Verfügbar</h4>
              <div className="space-y-1.5">
                {unselected.map(id => {
                  const def = getDef(id);
                  if (!def) return null;
                  const Icon = def.icon;
                  const atMax = draft.length >= MAX_KPIS;
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => toggle(id)}
                      disabled={atMax}
                      className="w-full flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-gray-200 hover:border-primary/40 hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <div className={`w-7 h-7 rounded-md flex items-center justify-center ${def.accent}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <span className="flex-1 text-sm font-medium text-left">{def.label}</span>
                      <span className="text-xs text-gray-400">{atMax ? `Max. ${MAX_KPIS}` : 'Hinzufügen'}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between px-5 py-3 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onResetDefault}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Auf Standard zurücksetzen
          </button>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              Abbrechen
            </button>
            <button
              type="button"
              onClick={() => onSave(draft)}
              disabled={draft.length < MIN_KPIS}
              className="px-4 py-2 text-sm font-medium bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Übernehmen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
